import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const user = await login(formData.email, formData.password);
            toast.success('Connexion réussie !');
            
            if (user.role === 'super_admin') navigate('/homly-internal');
            else if (user.role === 'group_manager') navigate('/group');
            else if (user.role === 'admin') navigate('/admin');
            else if (user.role === 'agent') navigate('/agent');
            else if (user.role === 'seller') navigate('/seller');
            else if (user.role === 'professional') navigate('/pro/dashboard');
            else navigate('/dashboard');
        } catch (err) {
            toast.error(err.message || 'Email ou mot de passe incorrect');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-white mb-2">Connexion</h2>
            <p className="text-sm text-muted-foreground mb-6">
                Accédez à votre espace de gestion
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <Label htmlFor="email" className="text-muted-foreground">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        placeholder="votre@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="bg-card border-border text-white placeholder:text-white/30"
                        data-testid="login-email-input"
                    />
                </div>

                <div>
                    <Label htmlFor="password" className="text-muted-foreground">Mot de passe</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                            className="bg-card border-border text-white placeholder:text-white/30 pr-10"
                            data-testid="login-password-input"
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-end">
                    <Link 
                        to="/forgot-password" 
                        className="text-sm text-[#FF914D] hover:underline"
                        data-testid="forgot-password-link"
                    >
                        Mot de passe oublié ?
                    </Link>
                </div>

                <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:from-[#7c2ba8] hover:to-[#ffab6d] text-white h-12 font-semibold shadow-lg shadow-[#681793]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#681793]/30 hover:scale-[1.01]" 
                    disabled={loading}
                    data-testid="login-submit-button"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connexion...
                        </>
                    ) : (
                        'Se connecter'
                    )}
                </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                    Pas encore de compte ?{' '}
                    <Link 
                        to="/agences?demo=true" 
                        className="text-[#FF914D] font-medium hover:underline"
                        data-testid="demo-request-link"
                    >
                        Demander une démo
                    </Link>
                </p>
                <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-white/10" />
                    </div>
                    <div className="relative flex justify-center">
                        <span className="bg-card px-2 text-[10px] tracking-[2px] text-muted-foreground">OU</span>
                    </div>
                </div>
                <Link
                    to="/register/solo"
                    data-testid="solo-register-link"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#A855F7]/30 bg-gradient-to-r from-[#681793]/15 to-[#FF914D]/15 text-sm font-semibold text-white hover:border-[#A855F7]/60 transition-colors"
                >
                    Je suis agent commercial indépendant →
                </Link>
            </div>
        </div>
    );
}

export default LoginPage;
